import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useState } from 'react'
import {
  FLOCK_EMAIL,
  FLOCK_PHONE_NUMBER,
  GOOGLE_PHONE_NUMBER,
  REDDIT_PHONE_NUMBER,
} from '../../constants'
import {
  HOMEPAGE_PATH,
  WHAT_IS_FLOCK_PATH,
  HOW_IT_WORKS_PATH,
  FAQ_PATH,
  STORIES_PATH,
  AGENTS_PATH,
  BLOG_PATH,
  PORTFOLIO_PATH,
  PRIVACY_PATH,
  ONBOARDING_PATH,
  TERMS_OF_USE_PATH,
  ABOUT_US_PATH,
  RENT_SELL_FLOCK_CALCULATOR_PATH,
  INVEST_WITH_FLOCK_PATH,
  FLOCK_CAREERS_URL,
} from '../../routeConstants'
import { getUtmSource } from '../../utils'
import { EqualHousingOpportunityIcon } from '../icons/EqualHousingOpportunityIcon'
import FacebookIcon from '../icons/FacebookIcon/FacebookIcon'
import InstagramIcon from '../icons/InstagramIcon/InstagramIcon'
import LinkedinIcon from '../icons/LinkedinIcon/LinkedinIcon'
import TwitterIcon from '../icons/TwitterIcon/TwitterIcon'
import { TrackedButton } from '../TrackedButton'
import TrackedLink from '../TrackedLink/TrackedLink'
import { TrackingContextProvider } from '../TrackingContextProvider'

const SocialBlock = () => (
  <>
    <TrackedLink
      aria-label="facebook"
      to="https://www.facebook.com/flockhomesinc/"
      target="_blank"
    >
      <FacebookIcon color="primary" />
    </TrackedLink>
    <TrackedLink
      aria-label="linkedin"
      to="https://www.linkedin.com/company/flock-homes/mycompany/"
      target="_blank"
    >
      <LinkedinIcon color="primary" />
    </TrackedLink>
    <TrackedLink
      to="https://www.instagram.com/flockhomes/?utm_source=website&utm_medium=&utm_campaign=instagram"
      target="_blank"
      aria-label="instagram"
    >
      <InstagramIcon color="primary" />
    </TrackedLink>
    <TrackedLink
      to="https://twitter.com/flockhomes?lang=en"
      target="_blank"
      aria-label="twitter"
    >
      <TwitterIcon color="primary" />
    </TrackedLink>
  </>
)

type TrackedFooterProps = {
  asOfDate?: Date
  hideNavItems?: boolean
  logoPath?: string
  phone?: string
  email?: string
  isSimple?: boolean
  displayCashFlowTerms?: boolean
}

const TrackedFooter = (props: TrackedFooterProps) => {
  const {
    asOfDate,
    hideNavItems,
    logoPath,
    phone,
    email,
    isSimple,
    displayCashFlowTerms,
  } = props
  let formattedAsOfDate = ''
  if (asOfDate) {
    const options: Intl.DateTimeFormatOptions = {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }
    formattedAsOfDate =
      new Date(asOfDate!).toLocaleDateString('en-US', options) || ''
  }

  const theme = useTheme()
  const [showFullDisclaimer, setShowFullDisclaimer] = useState<boolean>(false)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))

  let sourcedPhone = FLOCK_PHONE_NUMBER
  const utmSource = getUtmSource()

  if (utmSource === 'reddit') {
    sourcedPhone = REDDIT_PHONE_NUMBER
  } else if (utmSource === 'google') {
    sourcedPhone = GOOGLE_PHONE_NUMBER
  }

  // Simple layout
  if (isSimple) {
    return (
      <TrackingContextProvider name="footer">
        <Box
          sx={{
            width: '100%',
            backgroundColor: 'gray1.main',
            padding: isMobile ? '24px 0' : '32px 0',
          }}
          display="flex"
          flexDirection="column"
          gap="24px"
        >
          <TrackedLink
            variant="h4"
            to={logoPath || HOMEPAGE_PATH}
            sx={{ color: '#03341D!important' }}
          >
            {' '}
            F L O C K
          </TrackedLink>
          <Box
            display="flex"
            flexDirection={isMobile ? 'column' : 'row'}
            justifyContent="space-between"
            alignItems={isMobile ? 'flex-start' : 'center'}
            gap={isMobile ? '16px' : '0'}
            width="100%"
          >
            {/* Contact Block */}
            <Box display="flex" flexDirection="column">
              <TrackedLink
                to="#"
                onClick={() => window.open(`tel:${phone || sourcedPhone}`)}
                variant="p2"
              >
                {phone || sourcedPhone}
              </TrackedLink>
              <TrackedLink
                to="#"
                onClick={() => window.open(`mailto:${email || FLOCK_EMAIL}`)}
                variant="p2"
              >
                {email || FLOCK_EMAIL}
              </TrackedLink>
            </Box>
            {/* Social Block */}
            <Box display="flex" flexDirection="row" gap="8px">
              <SocialBlock />
            </Box>
          </Box>
          {/* Disclaimer at the bottom */}
          <Box
            width="100%"
            display="flex"
            flexDirection={isMobile ? 'column' : 'row'}
            alignItems="flex-start"
            gap={isMobile ? '16px' : '32px'}
          >
            <Box width="64px" display="flex" alignItems="center">
              <EqualHousingOpportunityIcon
                width={64}
                height={64}
                color="gray1"
              />
            </Box>
            <Box
              width="100%"
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              gap="8px"
            >
              {displayCashFlowTerms && !isMobile && (
                <Typography
                  variant="p3"
                  sx={{ color: 'gray8.main', fontWeight: 300 }}
                >
                  Cash flow, supplemental cash flow, and supplemental cash flow
                  charge are referred to as &quot;As-of-Right Redemption,&quot;
                  &quot;Top Up Redemption,&quot; and &quot;Top Up Redemption
                  Charge&quot; respectively, in the Flock Limited Partnership
                  Agreement. All figures subject to change as material facts
                  change.
                </Typography>
              )}
              <Typography
                variant="p3"
                sx={{ color: 'gray8.main', fontWeight: 300 }}
              >
                Performance Disclaimer: In considering the target performance
                information contained herein, prospective investors should bear
                in mind that past or targeted performance is not a guarantee,
                projection or prediction and is not necessarily indicative of
                future results. There can be no assurance that the Fund will
                achieve comparable results, that targeted returns will be met or
                that the Fund will be able to implement its investment strategy
                and investment approach or achieve its investment objectives.
                Actual gross and net returns for the Fund may vary significantly
                from the targeted returns set forth herein.
              </Typography>
              <Typography
                variant="p3"
                sx={{ color: 'gray8.main', fontWeight: 300 }}
              >
                Disclaimer: All content presented here is purely informational.
                It is not personalized investment advice, nor should it be
                construed as financial, legal, or tax advice. It is intended for
                information gathering only. The information presented here is
                not an offer to buy or sell securities or a solicitation of any
                offer to buy or sell securities. A professional financial
                advisor, attorney, and/or tax professional should be consulted
                regarding your specific financial, legal, and/or tax situation.{' '}
                {formattedAsOfDate &&
                  `Please also
                    note that certain visuals and metrics included on this site
                    have been calculated and are only up to date as of
                    ${formattedAsOfDate}.`}
              </Typography>
              <Box
                width="100%"
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap="32px"
              >
                <TrackedLink to={TERMS_OF_USE_PATH} variant="p2">
                  Terms of Use
                </TrackedLink>
                <TrackedLink to={PRIVACY_PATH} variant="p2">
                  Privacy
                </TrackedLink>
                <Typography
                  variant="p2"
                  sx={{ color: 'gray8.main', fontWeight: 300 }}
                >
                  © 2025 Flock Homes, Inc.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </TrackingContextProvider>
    )
  }

  if (isMobile) {
    return (
      <TrackingContextProvider name="footer">
        <Box
          sx={{
            backgroundColor: 'gray1.main',
          }}
          width="100%"
          flexDirection="column"
          display="flex"
          gap="24px"
        >
          <Box width="304px" flexDirection="column" display="flex" gap="16px">
            <TrackedLink
              variant="h4"
              to={logoPath || HOMEPAGE_PATH}
              sx={{ color: '#03341D!important' }}
            >
              {' '}
              F L O C K
            </TrackedLink>
            <Box>
              {phone ? (
                <TrackedLink
                  onClick={() => window.open(`tel:${phone}`)}
                  to="#"
                  variant="p2"
                >
                  {phone}
                </TrackedLink>
              ) : (
                <TrackedLink
                  onClick={() => window.open(`tel:${sourcedPhone}`)}
                  to="#"
                  variant="p2"
                >
                  {sourcedPhone}
                </TrackedLink>
              )}
              <br />
              {email ? (
                <TrackedLink
                  to="#"
                  onClick={() => window.open(`mailto:${email}`)}
                  variant="p2"
                >
                  {email}
                </TrackedLink>
              ) : (
                <TrackedLink
                  to="#"
                  onClick={() => window.open(`mailto:${FLOCK_EMAIL}`)}
                  variant="p2"
                >
                  {FLOCK_EMAIL}
                </TrackedLink>
              )}
            </Box>
          </Box>

          {hideNavItems ? null : (
            <Box
              width="304px"
              flexDirection="row"
              display="flex"
              justifyContent="space-between"
              gap="24px"
            >
              <Box
                width="124px"
                flexDirection="column"
                display="flex"
                gap="16px"
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 500,
                    fontFamily: 'Outfit!important',
                    color: 'gray5.main',
                  }}
                >
                  For Landlords
                </Typography>
                <TrackedLink to={WHAT_IS_FLOCK_PATH} variant="p2">
                  What is Flock
                </TrackedLink>
                <TrackedLink to={HOW_IT_WORKS_PATH} variant="p2">
                  How it Works
                </TrackedLink>
                <TrackedLink to={FAQ_PATH} variant="p2">
                  FAQ
                </TrackedLink>

                <TrackedLink to={RENT_SELL_FLOCK_CALCULATOR_PATH} variant="p2">
                  Sale Calculator
                </TrackedLink>
                <TrackedLink to={AGENTS_PATH} variant="p2">
                  For Agents
                </TrackedLink>
                <TrackedLink to={STORIES_PATH} variant="p2">
                  Client stories
                </TrackedLink>
              </Box>
              <Box
                width="133px"
                flexDirection="column"
                display="flex"
                gap="16px"
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 500,
                    color: 'gray5.main',
                    fontFamily: 'Outfit!important',
                  }}
                >
                  Investment
                </Typography>
                <TrackedLink to={PORTFOLIO_PATH} variant="p2">
                  Portfolio
                </TrackedLink>
                <TrackedLink to={INVEST_WITH_FLOCK_PATH} variant="p2">
                  Invest with Flock
                </TrackedLink>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 500,
                    color: 'gray5.main',
                    fontFamily: 'Outfit!important',
                  }}
                >
                  About
                </Typography>
                <TrackedLink to={ABOUT_US_PATH} variant="p2">
                  About Us
                </TrackedLink>
                <TrackedLink to={FLOCK_CAREERS_URL} variant="p2">
                  Careers
                </TrackedLink>
                <TrackedLink to={BLOG_PATH} variant="p2">
                  The Flock Blog
                </TrackedLink>
              </Box>
            </Box>
          )}
          {hideNavItems ? null : (
            <Box
              width="240px"
              display="flex"
              justifyContent="space-between"
              marginLeft={isMobile ? '0px' : '37px'}
              alignSelf="center"
            >
              <SocialBlock />
            </Box>
          )}
          <Box
            width="304px"
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            gap="8px"
          >
            <EqualHousingOpportunityIcon width={64} height={64} color="gray1" />
            <Typography
              variant="p2"
              sx={{
                color: 'gray8.main',
                fontWeight: 300,
                marginTop: '8px',
              }}
            >
              For information purposes only.
              <TrackedButton
                variant="text"
                onClick={() => setShowFullDisclaimer(true)}
              >
                &nbsp;Learn more about our website content.
              </TrackedButton>
              {showFullDisclaimer ? (
                <>
                  <Typography
                    variant="p3"
                    sx={{ color: 'gray8.main', fontWeight: 300 }}
                  >
                    Performance Disclaimer: In considering the target
                    performance information contained herein, prospective
                    investors should bear in mind that past or targeted
                    performance is not a guarantee, projection or prediction and
                    is not necessarily indicative of future results. There can
                    be no assurance that the Fund will achieve comparable
                    results, that targeted returns will be met or that the Fund
                    will be able to implement its investment strategy and
                    investment approach or achieve its investment objectives.
                    Actual gross and net returns for the Fund may vary
                    significantly from the targeted returns set forth herein.
                    <br />
                    Disclaimer: All content presented here is purely
                    informational. It is not personalized investment advice, nor
                    should it be construed as financial, legal, or tax advice.
                    It is intended for information gathering only. The
                    information presented here is not an offer to buy or sell
                    securities or a solicitation of any offer to buy or sell
                    securities. A professional financial advisor, attorney,
                    and/or tax professional should be consulted regarding your
                    specific financial, legal, and/or tax situation.{' '}
                    {formattedAsOfDate &&
                      `Please also
                    note that certain visuals and metrics included on this site
                    have been calculated and are only up to date as of
                    ${formattedAsOfDate}.`}
                  </Typography>
                  <br />
                  <br />
                  <TrackedLink onClick={() => setShowFullDisclaimer(false)}>
                    Collapse &#8212;
                  </TrackedLink>
                  <br />
                  <br />
                </>
              ) : (
                <></>
              )}
            </Typography>
            <Box
              sx={{
                width: '100%',
              }}
              display="flex"
              gap="56px"
            >
              <TrackedLink to={TERMS_OF_USE_PATH}>
                <Typography variant="p2" sx={{ color: 'primary.main' }}>
                  Terms of Use
                </Typography>
              </TrackedLink>
              <TrackedLink to={PRIVACY_PATH}>
                <Typography variant="p2" sx={{ color: 'primary.main' }}>
                  Privacy
                </Typography>
              </TrackedLink>
            </Box>
            <Typography
              variant="p2"
              sx={{
                color: 'gray8.main',
                fontWeight: 300,
                marginTop: '8px',
              }}
            >
              © 2025 Flock Homes, Inc.
            </Typography>
          </Box>
        </Box>
      </TrackingContextProvider>
    )
  }

  if (isTablet) {
    return (
      <TrackingContextProvider name="footer">
        <Box
          sx={{
            width: '100%',
            backgroundColor: 'gray1.main',
          }}
          flexDirection="column"
          display="flex"
          gap="24px"
        >
          <Box display="flex" justifyContent="space-between">
            <Box width="352px" flexDirection="column" display="flex" gap="16px">
              <TrackedLink
                variant="h4"
                to={logoPath || HOMEPAGE_PATH}
                sx={{ color: '#03341D!important' }}
              >
                F L O C K
              </TrackedLink>
              <Box display="flex" flexDirection="column" gap="8px">
                <Typography variant="p2" sx={{ fontWeight: '500' }}>
                  Questions? <br />
                </Typography>
                {phone ? (
                  <TrackedLink
                    onClick={() => window.open(`tel:${phone}`)}
                    to="#"
                  >
                    {phone}
                  </TrackedLink>
                ) : (
                  <TrackedLink
                    onClick={() => window.open(`tel:${sourcedPhone}`)}
                    to="#"
                  >
                    {sourcedPhone}
                  </TrackedLink>
                )}

                <Typography variant="p2" sx={{ color: 'primary.main' }}>
                  Email us at{' '}
                  {email ? (
                    <TrackedLink
                      onClick={() => window.open(`mailto:${email}`)}
                      to="#"
                    >
                      {email}
                    </TrackedLink>
                  ) : (
                    <TrackedLink
                      onClick={() => window.open(`mailto:${FLOCK_EMAIL}`)}
                      to="#"
                    >
                      {FLOCK_EMAIL}
                    </TrackedLink>
                  )}
                </Typography>
              </Box>
              {hideNavItems ? null : (
                <Box
                  width="240px"
                  display="flex"
                  justifyContent="space-between"
                >
                  <SocialBlock />
                </Box>
              )}
            </Box>
          </Box>

          {hideNavItems ? null : (
            <Box
              width="100%"
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
            >
              <Box
                width="140px"
                display="flex"
                flexDirection="column"
                gap="16px"
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 500,
                    color: 'gray5.main',
                    fontFamily: 'Outfit!important',
                  }}
                >
                  For Landlords
                </Typography>
                <TrackedLink to={WHAT_IS_FLOCK_PATH} variant="p2">
                  What is Flock
                </TrackedLink>
                <TrackedLink to={HOW_IT_WORKS_PATH} variant="p2">
                  How it Works
                </TrackedLink>
                <TrackedLink to={FAQ_PATH} variant="p2">
                  FAQ
                </TrackedLink>
                <TrackedLink to={RENT_SELL_FLOCK_CALCULATOR_PATH} variant="p2">
                  Sale Calculator
                </TrackedLink>
                <TrackedLink to={AGENTS_PATH} variant="p2">
                  For Agents
                </TrackedLink>
                <TrackedLink to={STORIES_PATH} variant="p2">
                  Client stories
                </TrackedLink>
              </Box>
              <Box
                width="106px"
                display="flex"
                flexDirection="column"
                gap="16px"
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 500,
                    color: 'gray5.main',
                    fontFamily: 'Outfit!important',
                  }}
                >
                  Investment
                </Typography>
                <TrackedLink to={PORTFOLIO_PATH} variant="p2">
                  Portfolio
                </TrackedLink>
                <TrackedLink to={INVEST_WITH_FLOCK_PATH} variant="p2">
                  Invest with Flock
                </TrackedLink>
              </Box>
              <Box
                width="105px"
                display="flex"
                flexDirection="column"
                gap="16px"
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 500,
                    color: 'gray5.main',
                    fontFamily: 'Outfit!important',
                  }}
                >
                  About
                </Typography>
                <TrackedLink to={ABOUT_US_PATH} variant="p2">
                  About Us
                </TrackedLink>
                <TrackedLink to={FLOCK_CAREERS_URL} variant="p2">
                  Careers
                </TrackedLink>
                <TrackedLink to={BLOG_PATH} variant="p2">
                  Flock Blog
                </TrackedLink>
              </Box>
              <Box
                width="108px"
                display="flex"
                flexDirection="column"
                gap="16px"
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 500,
                    color: 'gray5.main',
                    fontFamily: 'Outfit!important',
                  }}
                >
                  Sign in
                </Typography>
                <TrackedLink to={ONBOARDING_PATH} variant="p2">
                  Sign up
                </TrackedLink>
                <TrackedLink
                  onClick={() =>
                    window.open('https://investor.flockhomes.com/')
                  }
                  variant="p2"
                >
                  Investor sign in
                </TrackedLink>
              </Box>
            </Box>
          )}
          <Box
            sx={{
              width: '100%',
            }}
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            gap="32px"
          >
            <Box width="64px" display="flex" alignItems="flex-start">
              <EqualHousingOpportunityIcon
                width={64}
                height={64}
                color="gray1"
              />
            </Box>
            <Box
              width="100%"
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              gap="8px"
            >
              {displayCashFlowTerms && (
                <Typography
                  variant="p3"
                  sx={{ color: 'gray8.main', fontWeight: 300 }}
                >
                  Cash flow, supplemental cash flow, and supplemental cash flow
                  charge are referred to as &quot;As-of-Right Redemption,&quot; &quot;Top Up
                  Redemption,&quot; and &quot;Top Up Redemption Charge&quot; respectively, in
                  the Flock Limited Partnership Agreement. All figures subject
                  to change as material facts change.
                </Typography>
              )}
              <Typography
                variant="p2"
                sx={{
                  color: 'gray8.main',
                  fontWeight: 300,
                }}
              >
                For information purposes only.
              </Typography>
              <TrackedButton
                variant="text"
                sx={{ fontWeight: 300, fontSize: '16px' }}
                onClick={() => setShowFullDisclaimer(true)}
              >
                &nbsp;Learn more about our website content.
              </TrackedButton>
              {showFullDisclaimer ? (
                <>
                  <Typography
                    variant="p3"
                    sx={{ color: 'gray8.main', fontWeight: 300 }}
                  >
                    Performance Disclaimer: In considering the target
                    performance information contained herein, prospective
                    investors should bear in mind that past or targeted
                    performance is not a guarantee, projection or prediction and
                    is not necessarily indicative of future results. There can
                    be no assurance that the Fund will achieve comparable
                    results, that targeted returns will be met or that the Fund
                    will be able to implement its investment strategy and
                    investment approach or achieve its investment objectives.
                    Actual gross and net returns for the Fund may vary
                    significantly from the targeted returns set forth herein.
                    <br />
                    Disclaimer: All content presented here is purely
                    informational. It is not personalized investment advice, nor
                    should it be construed as financial, legal, or tax advice.
                    It is intended for information gathering only. The
                    information presented here is not an offer to buy or sell
                    securities or a solicitation of any offer to buy or sell
                    securities. A professional financial advisor, attorney,
                    and/or tax professional should be consulted regarding your
                    specific financial, legal, and/or tax situation.{' '}
                    {formattedAsOfDate &&
                      `Please also
                    note that certain visuals and metrics included on this site
                    have been calculated and are only up to date as of
                    ${formattedAsOfDate}.`}
                  </Typography>
                  <br />
                  <br />
                  <TrackedLink onClick={() => setShowFullDisclaimer(false)}>
                    Collapse &#8212;
                  </TrackedLink>
                  <br />
                  <br />
                </>
              ) : (
                <></>
              )}
              <Box display="flex" alignItems="center" gap="32px">
                <TrackedLink to={TERMS_OF_USE_PATH} variant="p2">
                  Terms of Use
                </TrackedLink>
                <TrackedLink to={PRIVACY_PATH} variant="p2">
                  Privacy
                </TrackedLink>
                <Typography
                  variant="p2"
                  sx={{
                    color: 'gray8.main',
                    fontWeight: 300,
                  }}
                >
                  © 2025 Flock Homes, Inc.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </TrackingContextProvider>
    )
  }

  return (
    // non-mobile
    <TrackingContextProvider name="footer">
      <Box
        sx={{
          width: '100%',
          backgroundColor: 'gray1.main',
        }}
        flexDirection="column"
        display="flex"
        gap="24px"
      >
        <Box display="flex" justifyContent="space-between">
          <Box width="352px" flexDirection="column" display="flex" gap="16px">
            <TrackedLink
              variant="h4"
              to={logoPath || HOMEPAGE_PATH}
              sx={{ color: '#03341D!important' }}
            >
              {' '}
              F L O C K
            </TrackedLink>
            <Box display="flex" flexDirection="column" gap="8px">
              <Typography variant="p2" sx={{ fontWeight: '500' }}>
                Questions? <br />
              </Typography>
              {phone ? (
                <TrackedLink to="#" onClick={() => window.open(`tel:${phone}`)}>
                  {phone}
                </TrackedLink>
              ) : (
                <TrackedLink
                  to="#"
                  onClick={() => window.open(`tel:${sourcedPhone}`)}
                >
                  {sourcedPhone}
                </TrackedLink>
              )}

              <Typography variant="p2" sx={{ color: 'primary.main' }}>
                {email ? (
                  <TrackedLink
                    to="#"
                    onClick={() => window.open(`mailto:${email}`)}
                  >
                    {email}
                  </TrackedLink>
                ) : (
                  <TrackedLink
                    to="#"
                    onClick={() => window.open(`mailto:${FLOCK_EMAIL}`)}
                  >
                    {FLOCK_EMAIL}
                  </TrackedLink>
                )}
              </Typography>
            </Box>

            {hideNavItems ? null : (
              <Box width="240px" display="flex" justifyContent="space-between">
                <SocialBlock />
              </Box>
            )}
          </Box>

          {hideNavItems ? null : (
            <Box display="flex" flexDirection="row" gap="64px">
              <Box
                width="160px"
                display="flex"
                flexDirection="column"
                gap="16px"
              >
                <Typography
                  variant="cta2"
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 500,
                    color: 'green3.main',
                    fontFamily: 'Outfit!important',
                  }}
                >
                  For Landlords
                </Typography>
                <TrackedLink
                  to={WHAT_IS_FLOCK_PATH}
                  variant="p2"
                  sx={{ color: '#03341D!important' }}
                >
                  What is Flock
                </TrackedLink>
                <TrackedLink
                  to={HOW_IT_WORKS_PATH}
                  variant="p2"
                  sx={{ color: '#03341D!important' }}
                >
                  How it Works
                </TrackedLink>
                <TrackedLink
                  to={FAQ_PATH}
                  variant="p2"
                  sx={{ color: '#03341D!important' }}
                >
                  FAQ
                </TrackedLink>
                <TrackedLink
                  to={RENT_SELL_FLOCK_CALCULATOR_PATH}
                  variant="p2"
                  sx={{ color: '#03341D!important' }}
                >
                  Sale Calculator
                </TrackedLink>
                <TrackedLink
                  to={AGENTS_PATH}
                  variant="p2"
                  sx={{ color: '#03341D!important' }}
                >
                  For Agents
                </TrackedLink>
                <Box
                  width="32px"
                  height="2px"
                  sx={{ backgroundColor: 'moneyGreen.main' }}
                />
                <TrackedLink
                  to={STORIES_PATH}
                  variant="p2"
                  sx={{ color: '#03341D!important' }}
                >
                  Client Stories
                </TrackedLink>
              </Box>
              <Box
                width="160px"
                display="flex"
                flexDirection="column"
                gap="16px"
              >
                <Typography
                  variant="cta2"
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 500,
                    color: 'green3.main',
                    fontFamily: 'Outfit!important',
                  }}
                >
                  Investment
                </Typography>
                <TrackedLink
                  to={PORTFOLIO_PATH}
                  variant="p2"
                  sx={{ color: '#03341D!important' }}
                >
                  Portfolio
                </TrackedLink>
                <TrackedLink
                  to={INVEST_WITH_FLOCK_PATH}
                  variant="p2"
                  sx={{ color: '#03341D!important' }}
                >
                  Invest with Flock
                </TrackedLink>
              </Box>
              <Box
                width="160px"
                display="flex"
                flexDirection="column"
                gap="16px"
              >
                <Typography
                  variant="cta2"
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 500,
                    color: 'green3.main',
                    fontFamily: 'Outfit!important',
                  }}
                >
                  About
                </Typography>
                <TrackedLink
                  to={ABOUT_US_PATH}
                  variant="p2"
                  sx={{ color: '#03341D!important' }}
                >
                  About Us
                </TrackedLink>
                <TrackedLink
                  to={FLOCK_CAREERS_URL}
                  variant="p2"
                  sx={{ color: '#03341D!important' }}
                >
                  Careers
                </TrackedLink>
                <Box
                  width="32px"
                  height="2px"
                  sx={{ backgroundColor: 'moneyGreen.main' }}
                />
                <TrackedLink
                  to={BLOG_PATH}
                  variant="p2"
                  sx={{ color: '#03341D!important' }}
                >
                  The Flock Blog
                </TrackedLink>
              </Box>
              <Box
                width="160px"
                display="flex"
                flexDirection="column"
                gap="16px"
              >
                <Typography
                  variant="cta2"
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 500,
                    color: 'green3.main',
                  }}
                >
                  Account
                </Typography>
                <TrackedLink
                  to="https://investor.flockhomes.com/"
                  variant="p2"
                  sx={{ color: '#03341D!important' }}
                >
                  Sign In
                </TrackedLink>
                <TrackedLink
                  to={ONBOARDING_PATH}
                  variant="p2"
                  sx={{ color: '#03341D!important' }}
                >
                  Get Started
                </TrackedLink>
              </Box>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            width: '100%',
          }}
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          marginTop={hideNavItems ? '0px' : '77px'}
          gap="32px"
        >
          <Box width="64px" display="flex" alignItems="center">
            <EqualHousingOpportunityIcon width={64} height={64} color="gray1" />
          </Box>
          <Box
            width="100%"
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            gap="8px"
          >
            {displayCashFlowTerms && (
              <Typography
                variant="p3"
                sx={{ color: 'gray8.main', fontWeight: 300 }}
              >
                Cash flow, supplemental cash flow, and supplemental cash flow
                charge are referred to as &quot;As-of-Right Redemption,&quot; &quot;Top Up
                Redemption,&quot; and &quot;Top Up Redemption Charge&quot; respectively, in the
                Flock Limited Partnership Agreement. All figures subject to
                change as material facts change.
              </Typography>
            )}
            <Typography
              variant="p3"
              sx={{ color: 'gray8.main', fontWeight: 300 }}
            >
              Performance Disclaimer: In considering the target performance
              information contained herein, prospective investors should bear in
              mind that past or targeted performance is not a guarantee,
              projection or prediction and is not necessarily indicative of
              future results. There can be no assurance that the Fund will
              achieve comparable results, that targeted returns will be met or
              that the Fund will be able to implement its investment strategy
              and investment approach or achieve its investment objectives.
              Actual gross and net returns for the Fund may vary significantly
              from the targeted returns set forth herein.
            </Typography>
            <Typography
              variant="p3"
              sx={{ color: 'gray8.main', fontWeight: 300 }}
            >
              Disclaimer: All content presented here is purely informational. It
              is not personalized investment advice, nor should it be construed
              as financial, legal, or tax advice. It is intended for information
              gathering only. The information presented here is not an offer to
              buy or sell securities or a solicitation of any offer to buy or
              sell securities. A professional financial advisor, attorney,
              and/or tax professional should be consulted regarding your
              specific financial, legal, and/or tax situation.{' '}
              {formattedAsOfDate &&
                `Please also
                    note that certain visuals and metrics included on this site
                    have been calculated and are only up to date as of
                    ${formattedAsOfDate}.`}
            </Typography>

            <Box
              width="100%"
              display="flex"
              flexDirection="row"
              alignItems="center"
              gap="32px"
            >
              <Box
                width="100%"
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap="32px"
              >
                <TrackedLink to={TERMS_OF_USE_PATH} variant="p2">
                  Terms of Use
                </TrackedLink>
                <TrackedLink to={PRIVACY_PATH} variant="p2">
                  Privacy
                </TrackedLink>
              </Box>
              <Box
                width="381px"
                display="flex"
                flexDirection="row"
                alignItems="right"
                gap="32px"
              >
                <Typography
                  variant="p2"
                  sx={{ color: 'gray8.main', fontWeight: 300 }}
                >
                  © 2025 Flock Homes, Inc.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </TrackingContextProvider>
  )
}

TrackedFooter.defaultProps = {
  asOfDate: undefined,
  hideNavItems: false,
  logoPath: undefined,
  email: '',
  phone: '',
  isSimple: false,
  displayCashFlowTerms: false,
}

export default TrackedFooter
